/**
 * Single source of launch-time content for the WatchAI pages. Everything a
 * non-developer needs to touch at launch lives here, so publishing the app,
 * bumping the version, or adding screenshots is a content edit — not a code
 * change.
 *
 * To go live:
 *   1. Set PLAY_URL to the real Play Store listing URL (this activates every
 *      "Get it on Google Play" button automatically).
 *   2. Add screenshot entries to SCREENSHOTS (drop the files in /public).
 *   3. Bump VERSION and prepend a CHANGELOG entry.
 */

// null = not published yet. When set, every download CTA becomes a live link
// and the structured-data offer is marked available.
export const PLAY_URL: string | null = null;

// The app's package id — used to build the Play URL and structured data.
export const ANDROID_PACKAGE = 'com.chatgptwatch.app';

export const VERSION = '1.0';
export const PRICE_USD = '1.99';
// Owner-designated public contact (the same verified address used as the Play
// Console public contact). The site still has other, conflicting addresses in the
// footer and homepage Contact section — documented in WEBSITE_AUDIT.md — which are
// left unchanged pending the owner's decision. Takes effect on the next deploy.
export const SUPPORT_EMAIL = '247@247ai360.com';

// Canonical host the site resolves to (apex is used in the existing site's
// <link rel="canonical">; keep the app pages consistent with that).
export const SITE_ORIGIN = 'https://247ai360.com';
const SCREENSHOT_ORIGIN = 'https://24-7-ai-360-project-ef947wcht-24-7-ai-360.vercel.app';

export interface Screenshot {
  /** Path under /public, e.g. "/screenshots/watch-1-home.png". */
  src: string;
  /** Required, meaningful alt text for accessibility. */
  alt: string;
  /** 'watch' or 'phone' — the gallery groups and frames by device. */
  device: 'watch' | 'phone';
  /** Short caption shown under the image. */
  caption: string;
  /** Intrinsic pixel size of the file, so the layout reserves space (no CLS). */
  w: number;
  h: number;
}

// Real screenshots captured from the RC1 build during device acceptance testing.
// To add or replace: drop the file in /public/screenshots, add an entry here with
// its real pixel dimensions — the gallery restructures itself automatically and
// hides entirely if this array is emptied.
export const SCREENSHOTS: Screenshot[] = [
  {
    device: 'watch',
    src: `${SCREENSHOT_ORIGIN}/screenshots/watch-1-home.png`,
    caption: 'Home on your wrist',
    alt: 'WatchAI home screen on a Wear OS watch with a New Chat button, a settings icon, and swipe hints to chat, scan and history.',
    w: 480, h: 480,
  },
  {
    device: 'watch',
    src: `${SCREENSHOT_ORIGIN}/screenshots/watch-2-conversation.png`,
    caption: 'Ask by voice or text',
    alt: 'A WatchAI conversation on the watch: the question “How do you say good morning in Japanese” answered with “Ohayō gozaimasu”, with the microphone button below.',
    w: 480, h: 480,
  },
  {
    device: 'watch',
    src: `${SCREENSHOT_ORIGIN}/screenshots/watch-3-scan.png`,
    caption: 'Snap, scan & describe',
    alt: 'WatchAI Extras page on the watch showing Snap & Describe, Scan & Answer, and From Gallery options.',
    w: 480, h: 480,
  },
  {
    device: 'watch',
    src: `${SCREENSHOT_ORIGIN}/screenshots/watch-4-history.png`,
    caption: 'Saved history',
    alt: 'WatchAI history on the watch listing recent questions, each with a star and a delete button.',
    w: 480, h: 480,
  },
  {
    device: 'phone',
    src: `${SCREENSHOT_ORIGIN}/screenshots/phone-1-setup.png`,
    caption: 'Free setup on your phone',
    alt: 'WatchAI phone companion setup, step one: “Get your free AI key” with Google Gemini, OpenAI or Anthropic.',
    w: 720, h: 1406,
  },
  {
    device: 'phone',
    src: `${SCREENSHOT_ORIGIN}/screenshots/phone-2-provider.png`,
    caption: 'Choose your AI provider',
    alt: 'WatchAI phone companion showing the AI provider set to Google — Gemini & Gemma (free), with an Open Key Page button.',
    w: 720, h: 1406,
  },
  {
    device: 'phone',
    src: `${SCREENSHOT_ORIGIN}/screenshots/phone-3-settings.png`,
    caption: 'Provider, model & options',
    alt: 'WatchAI phone settings: provider Google Gemini, model gemini-2.5-flash, mic input language auto-detect, and concise replies enabled. The API key field is masked.',
    w: 720, h: 1220,
  },
];

export interface ChangelogEntry {
  version: string;
  date: string;
  notes: string[];
}

export const CHANGELOG: ChangelogEntry[] = [
  {
    version: '1.0',
    date: 'Initial release',
    notes: [
      'Voice, camera scan and text chat on Wear OS',
      'Google Gemini, OpenAI and Anthropic Claude support',
      'Encrypted bring-your-own-key setup via the phone companion',
      'Phone-mic transcription while the screen is locked',
    ],
  },
];
